/**********************************************************************************************************
 *   BASE IMPORTS
 **********************************************************************************************************/
import { RefObject, useEffect, useState } from 'react'
import { Field, Form, Formik, FormikProps } from 'formik'
import styled from 'styled-components'
import { PlusCircleIcon } from '@heroicons/react/solid'

/**********************************************************************************************************
 *   SHARED IMPORTS
 **********************************************************************************************************/
import { Form as NXUIForm } from '@nexigendigital/nxui'
import { useAccountPaymentMethodsQuery } from 'api/billing'
import { setCheckoutStep, setPaymentMethod } from 'store/slices/shopSlice'
import { PaymentMethodData } from 'models/billing'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { parseISO } from 'helpers/utils'

import NewPaymentMethodLightbox from 'containers/shop/checkout/lightbox/newPaymentMethodLightbox'
import RemovePaymentConfirmationLightbox from 'containers/shop/checkout/lightbox/removePaymentConfirmationLightbox'

/**********************************************************************************************************
 *   INTERFACE
 **********************************************************************************************************/
interface Props {
    paymentMethodRef: RefObject<FormikProps<{ card: string | null }>>
}

/**********************************************************************************************************
 *   COMPONENT START
 **********************************************************************************************************/
export default function PaymentList({ paymentMethodRef }: Props) {
    const dispatch = useAppDispatch()
    const [openLightbox, setOpenLightbox] = useState(false)
    const { appSession: { authenticated } } = useAppSelector((state) => state.app)
    const [cardList, setCardList] = useState([])
    const [openRemovePaymentLightbox, setOpenRemovePaymentLightbox] = useState(false)
    const [currentPayment, setCurrentPayment] = useState<string | null>(null)
    const { data, isSuccess } = useAccountPaymentMethodsQuery(undefined, {
        skip: !authenticated
    })

    useEffect(() => {
        if (isSuccess && data) {
            const defaultCard = data.find(({ is_default }: PaymentMethodData) => is_default)

            if (defaultCard && paymentMethodRef.current) {
                paymentMethodRef.current.setFieldValue('card', `card-${String(defaultCard.id)}`)
                dispatch(setPaymentMethod({ id: defaultCard.id, identifier: defaultCard.identifier }))
            }

            // @ts-ignore
            setCardList(data.map(({ id, identifier, expiry, is_default }: PaymentMethodData) => ({
                label: {
                    number: `**** **** ${identifier}`,
                    expiry: `${parseISO(expiry).month}/${parseISO(expiry).year}`,
                    default: is_default
                },
                value: id
            })))
        }
    }, [data, isSuccess, paymentMethodRef])

    return (
        <>
            <Formik
                innerRef={paymentMethodRef}
                initialValues={{
                    card: ''
                }}
                onSubmit={(values) => {
                    const cardId = values.card ? Number(values.card.replace('card-', '')) : 0

                    if (data) {
                        const selectedCard = data.find(({ id }: PaymentMethodData) => +id === cardId)

                        if (selectedCard) {
                            dispatch(setPaymentMethod({ id: selectedCard.id, identifier: selectedCard.identifier }))
                            dispatch(setCheckoutStep('checkout'))
                        }
                    }
                }}
            >
                {() => (
                    <Form>
                        <CreditCards>
                            {cardList.map((card: { label: string, value: string }) => {
                                return (
                                    <Field
                                        key={`card-${card.value}`}
                                        name={'card'}
                                        type={'radio'}
                                        label={card.label}
                                        value={`card-${card.value}`}
                                        action={() => {
                                            setOpenRemovePaymentLightbox(true)
                                            setCurrentPayment(card.value)
                                        }}
                                        component={NXUIForm.CardField}
                                    />
                                )
                            })}
                            <NewCard type={'button'} onClick={() => setOpenLightbox(true)}>
                                <Icon>
                                    <PlusCircleIcon/>
                                </Icon>
                            </NewCard>
                        </CreditCards>
                    </Form>
                )}
            </Formik>
            <NewPaymentMethodLightbox
                status={openLightbox}
                close={() => setOpenLightbox(false)}
            />
            <RemovePaymentConfirmationLightbox
                id={currentPayment}
                status={openRemovePaymentLightbox}
                close={() => setOpenRemovePaymentLightbox(false)}
            />
        </>
    )
}

/**********************************************************************************************************
 *   CUSTOM STYLING
 **********************************************************************************************************/
const CreditCards = styled.div`
    display: flex;
    gap: 24px;
`

const NewCard = styled.button`
    width: 280px;
    height: 174px;
    border-radius: 12px;
    background-color: #eef2ff;
    display: flex;
    align-items: center;
    justify-content: center;
`

const Icon = styled.div`
    > svg {
        width: 64px;
        height: 64px;
        color: #d6e1fe
    }
`
